import { useCallback } from 'react';
import { asyncify, doUntil } from 'async';

import useAbortControllerSignal from './useAbortControllerSignal';
import useLatestState from './useLatesetState';

export type DoWhileFn = (signal: AbortSignal) => Promise<boolean>;
const useDoWhile = (doWhile: DoWhileFn) => {
  const signal = useAbortControllerSignal();
  const [, setDoing] = useLatestState(false);

  const work = useCallback(async () => {
    if (setDoing((s) => s)) {
      return;
    }

    setDoing(true);

    let res: boolean;
    try {
      await doUntil(
        asyncify(async () => {
          res = await doWhile(signal);
        }),
        asyncify(() => res || signal.aborted),
      );
    } catch (err) {
      if (!signal.aborted) {
        throw err;
      }
    } finally {
      setDoing(false);
    }
  }, [doWhile, setDoing, signal]);

  return work;
};

export default useDoWhile;
